import React, { useState, useEffect, useCallback, useMemo } from 'react';

const ImageCarousal: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loadedImages, setLoadedImages] = useState<boolean[]>([]);

  const images = useMemo(() => [
    './Shindhant1.png',
    './Shindhant2.jpg',
    '/Shindhant3.jpg',
    '/Shindhant4.jpg',
    '/Shindhant5.jpg',
    '/Shindhant6.jpg',
    '/Shindhant7.jpg',
    '/Shindhant8.jpg',
    '/Shindhant9.jpg',
    '/Shindhant10.jpg',
    '/Shindhant11.jpg',
    '/Shindhant12.jpg',
    '/Shindhant13.jpg',
    '/Shindhant14.jpg',
    '/Shindhant15.jpg',
    '/Shindhant16.jpg',
    '/Shindhant17.jpg',
  ], []);

  // Preload images
  useEffect(() => {
    const loadImages = async () => {
      const loadPromises = images.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
        });
      });

      const results = await Promise.all(loadPromises);
      setLoadedImages(results as boolean[]);
    };

    loadImages();
  }, [images]);

  const goToNext = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
      setTimeout(() => setIsAnimating(false), 600);
    }
  }, [isAnimating, images.length]);

  const goToPrevious = useCallback(() => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) =>
        prevIndex === 0 ? images.length - 1 : prevIndex - 1
      );
      setTimeout(() => setIsAnimating(false), 600);
    }
  }, [isAnimating, images.length]);

  const goToSlide = useCallback(
    (index: number) => {
      if (!isAnimating && index !== currentIndex) {
        setIsAnimating(true);
        setCurrentIndex(index);
        setTimeout(() => setIsAnimating(false), 600);
      }
    },
    [isAnimating, currentIndex]
  );

  useEffect(() => {
    const interval = setInterval(goToNext, 2000); // Auto-advance every 2 seconds
    return () => clearInterval(interval);
  }, [goToNext]);

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-teal-50 py-16">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-amber-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            <span className="text-amber-500">Global</span>{' '}
            <span className="text-teal-700">Trade</span>{' '}
            <span className="text-amber-500">Excellence</span>
          </h1>
          <div className="w-24 h-1.5 bg-gradient-to-r from-amber-500 to-teal-600 rounded-full mx-auto mb-4"></div>
          
        </div>

        {/* Main Carousel Container */}
        <div className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl group">
          {/* Loading State */}
          {!loadedImages[currentIndex] && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-amber-100 to-teal-100">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Loading amazing content...</p>
              </div>
            </div>
          )}

          {/* Current Image */}
          <img
            src={images[currentIndex]}
            alt={`Trade ${currentIndex + 1}`}
            className={`w-full h-full object-cover transition-all duration-700 ease-out ${
              isAnimating ? 'scale-110 opacity-80' : 'scale-100 opacity-100'
            } ${!loadedImages[currentIndex] ? 'hidden' : 'block'}`}
          />

          {/* Enhanced Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>

        

          {/* Enhanced Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-4 rounded-full shadow-2xl transform transition-all duration-300 hover:scale-110 hover:shadow-2xl focus:outline-none group/arrow"
          >
            <svg
              className="w-6 h-6 transform group-hover/arrow:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-4 rounded-full shadow-2xl transform transition-all duration-300 hover:scale-110 hover:shadow-2xl focus:outline-none group/arrow"
          >
            <svg
              className="w-6 h-6 transform group-hover/arrow:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Enhanced Image Counter */}
          <div className="absolute top-6 right-6 bg-gradient-to-r from-amber-500 to-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
            <span className="text-amber-100">{currentIndex + 1}</span>
            <span className="text-white"> / </span>
            <span className="text-teal-100">{images.length}</span>
          </div>
        </div>

        {/* Enhanced Thumbnail Navigation */}
        <div className="mt-8 flex justify-center gap-3 overflow-x-auto py-4 px-4">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 relative group transition-all duration-300 ${
                index === currentIndex ? 'scale-110' : 'scale-100 hover:scale-105'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className={`w-16 h-12 object-cover rounded-lg shadow-md transition-all duration-300 ${
                  index === currentIndex
                    ? 'ring-4 ring-amber-500 opacity-100'
                    : 'opacity-70 hover:opacity-100 hover:ring-2 hover:ring-teal-400'
                }`}
              />
              {/* Progress Indicator */}
              {index === currentIndex && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-teal-600 rounded-full"></div>
              )}
            </button>
          ))}
        </div>

      
      </div>

      {/* Custom Animation Styles */}
      <style >{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(30px) scale(1.05);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        
        .carousel-slide {
          animation: slideIn 0.7s ease-out;
        }
        
        /* Custom scrollbar for thumbnails */
        .thumbnails-container::-webkit-scrollbar {
          height: 6px;
        }
        
        .thumbnails-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .thumbnails-container::-webkit-scrollbar-thumb {
          background: linear-gradient(to right, #f59e0b, #0d9488);
          border-radius: 10px;
        }
      `}</style>

      {/* Add custom class for thumbnails container */}
      <style>{`
        .thumbnails-container {
          scrollbar-width: thin;
          scrollbar-color: #f59e0b #f1f1f1;
        }
      `}</style>
    </div>
  );
};

export default ImageCarousal;